import * as React from 'react';
import { useEffect } from 'react';
import { userReducer } from './auth.reducer';
import { UserContext, UserInitialState } from './auth.context';
import { useLazyQuery } from '@apollo/client';
import {
  AuthenticatedUser,
  GET_USER_DATA,
  mapToUserFront,
  UserAction,
  UserStatus,
} from '@contexts/Shared';
import { NextRouter, useRouter } from 'next/router';

type UserAuthProviderProps = {
  children: React.ReactNode;
};

export function UserAuthProvider({ children }: UserAuthProviderProps) {
  const router = useRouter();
  const [userState, userStateDispatch] = React.useReducer(
    userReducer,
    UserInitialState,
    (arg) => {
      const token = resolveToken();
      return {
        token: token,
        status: arg.status,
        data: arg.data,
      };
    }
  );

  const handleErrors = (error: any) => {
    console.error('Error authenticating user', error);
    userStateDispatch({
      type: UserAction.USER_FETCH_ERROR_DATA_UPDATE,
      payload: { status: UserStatus.ERROR },
    });
    localStorage.removeItem('token');
  };

  /** User Authenticated Data */
  const [getAuthenticatedUser] = useLazyQuery<{
    getAuthenticatedUser: AuthenticatedUser;
  }>(GET_USER_DATA, {
    errorPolicy: 'all',
    fetchPolicy: 'no-cache',
    onError: (error) => {
      handleErrors(error);
    },
    onCompleted: (data) => {
      if (data && data.getAuthenticatedUser) {
        userStateDispatch({
          type: UserAction.USER_FETCH_DATA_UPDATE,
          payload: {
            data: mapToUserFront(data.getAuthenticatedUser),
            status: UserStatus.IS_LOGGED_IN,
          },
        });

        removeTokenFromUrl(router).catch((error) => {
          console.log('Error removing token from url', error);
        });
      } else {
        handleErrors(data);
      }
    },
  });

  useEffect(() => {
    if (!userState.token) {
      userStateDispatch({
        type: UserAction.USER_FETCH_ERROR_DATA_UPDATE,
        payload: { status: UserStatus.IS_NOT_LOGGED },
      });
    } else if (
      userState.token &&
      userState.status === UserStatus.VALIDATING_LOGIN
    ) {
      userStateDispatch({
        type: UserAction.USER_FETCHING_DATA_UPDATE,
        payload: { status: UserStatus.LOADING },
      });
      getAuthenticatedUser().catch((error) => {
        console.log('Error getting user data', error);
      });
    }
  }, [getAuthenticatedUser, userState.status, userState.token]);

  return (
    <UserContext.Provider value={{ ...userState, dispatch: userStateDispatch }}>
      {children}
    </UserContext.Provider>
  );
}

function resolveToken(): string | undefined {
  if (typeof window === 'undefined') return undefined;

  const tokenFromUrl = resolveTokenFromUrl();

  return tokenFromUrl || localStorage.getItem('token') || undefined;
}

const resolveTokenFromUrl = (): string | null => {
  const params = new URLSearchParams(window.location.search);
  const unsafeToken = params.get('token');

  if (unsafeToken) {
    const decodedToken = decodeURIComponent(unsafeToken);
    localStorage.setItem('token', decodedToken);
    return decodedToken;
  }

  return unsafeToken;
};

function removeTokenFromUrl(router: NextRouter) {
  const url = new URL(window.location.href);
  url.searchParams.delete('token');
  return router.replace(url.pathname + url.search);
}
